import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { Button } from "../ui/Button";
import { FullPageSpinner } from "../ui/Spinner";
import styles from "./AuthGate.module.css";
import { logoutState, useMeQuery } from "./session";
import { s } from "./strings";

function BootError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className={styles.error} role="alert">
      <p>{s.bootError}</p>
      <Button onClick={onRetry}>{s.retry}</Button>
    </div>
  );
}

/** Logged-in routes: spinner while /api/auth/me loads, /login?returnUrl= on 401. */
export function AuthGate({ children }: { children?: ReactNode }) {
  const me = useMeQuery();
  const { pathname, search, hash } = useLocation();
  if (me.isPending) return <FullPageSpinner />;
  if (me.isError) return <BootError onRetry={() => void me.refetch()} />;
  if (!me.data) {
    const here = `${pathname}${search}${hash}`;
    const plain = here === "/" || logoutState.inProgress;
    const to = plain ? "/login" : `/login?returnUrl=${encodeURIComponent(here)}`;
    return <Navigate to={to} replace />;
  }
  // Cleared once a session exists again (not in the redirect branch: StrictMode renders it twice).
  logoutState.inProgress = false;
  // Every shell page is workspace-scoped; with none left the only way on is creating one (routes.md `/`).
  if (me.data.workspaces.length === 0) return <Navigate to="/create-ws" replace />;
  return <>{children ?? <Outlet />}</>;
}

/** Guest-only routes (login, signup): a signed-in user goes home instead. */
export function GuestOnly({ children }: { children?: ReactNode }) {
  const me = useMeQuery();
  if (me.isPending) return <FullPageSpinner />;
  if (me.data) return <Navigate to="/" replace />;
  return <>{children ?? <Outlet />}</>;
}
